import Button from 'antd/lib/button';
import DatePicker from 'antd/lib/date-picker';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import message from 'antd/lib/message';
import Table from 'antd/lib/table';
import Tooltip from 'antd/lib/tooltip';
import moment from 'moment';
import * as React from 'react';
import withStyles from 'react-jss';
import { useHistory } from 'react-router-dom';
import { commonStyles } from 'src/Library/CommonStyles';
import { OrgTypes } from 'src/Library/Constants';
import { NextOrPrev, PaginationData, StylesProps } from 'src/Library/Types';
import { useGenericState } from 'src/Library/UseGenericState';
import { downloadDailyMisReports, fetchDailyMisReports } from 'src/api/shipmentExecution';
import { isOrgReliance } from 'src/api/utils';
import { getRfqBulkRequest } from 'src/routing/utils';
import { getLastColumnId, getLastSortedColumnValue } from 'src/utils/pagination';
import SimplePaginator from './SimplePaginator';

const RangePicker = DatePicker.RangePicker;

const styles = (theme) => ({
  ...commonStyles(theme),
  box: {
    padding: 10,
    width: '98.3%',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 5,
    marginLeft: 13,
  },
  filters: {
    ...commonStyles(theme).flexRow,
  },
  header: {
    ...commonStyles(theme).flexRow,
    justifyContent: 'space-between',
    marginRight: 10,
    marginBottom: 10,
  },
  table: {
    '& td': {
      verticalAlign: 'baseline',
      cursor: 'pointer',
      border: 'none',
    },
    '& .ant-table-thead': {
      whiteSpace: 'pre-line !important',
      backgroundColor: '#',
      border: `1px solid ${theme.colors.border}`,
      color: theme.colors.text,
      padding: 0,
    },
    '& .ant-table-selection-column': {
      borderRight: 'none !important',
    },
  },
});

/* eslint-disable react/no-unused-prop-types */
interface IProps extends StylesProps<ReturnType<typeof styles>> {
  viewType: OrgTypes;
  organisationName: string;
}
/* eslint-enable react/no-unused-prop-types */
const DailyMisReport = (props: IProps) => {
  const history = useHistory();

  const [misReport, setMisReport] = React.useState({
    columns: [],
    result: [],
    chartName: '',
  });

  const [pagination, setPagination] = useGenericState<PaginationData>({
    currentPageNumber: 1,
    nextOrPrev: 'first',
    isNextPresent: false,
    lastItemIdNext: 0,
    lastItemIdPrev: 0,
    lastSortedColumnValueNext: 0,
    lastSortedColumnValuePrev: 0,
    resultPerPage: 25,
    sortBy: 'updated_at',
  });

  const [dateRange, setDateRange] = React.useState<RangePickerValue>();
  const [downloading, setDownloading] = React.useState<boolean>(false);
  const [loading, setloading] = React.useState<boolean>(false);
  const [lastRequest, setLastRequest] = React.useState({});
  const [loadData, setLoadData] = React.useState<boolean>(false);

  const { viewType, classes } = props;
  const { columns, result, chartName } = misReport || {};

  const getApiCallObject = () => {
    return {
      viewType,
      currentPageNumber: pagination.currentPageNumber,
      nextOrPrev: pagination.nextOrPrev,
      resultPerPage: pagination.resultPerPage,
      lastRowId: getLastColumnId(pagination),
      lastSortedColumnValue: getLastSortedColumnValue(pagination),
      createdAt: {
        startDate: dateRange?.[0] ? moment(dateRange?.[0]).startOf('day').valueOf() : undefined,
        endDate: dateRange?.[1] ? moment(dateRange?.[1]).endOf('day').valueOf() : undefined,
      }
    };
  };

  const loadMisReports = async () => {
    setloading(true);
    const body = getApiCallObject();
    const response = await fetchDailyMisReports(body);
    if (response.isSuccess) {
      setLastRequest(body);
      setMisReport(response.data);
      const {
        lastItemIdNext,
        lastItemIdPrev,
        lastSortedColumnValueNext,
        lastSortedColumnValuePrev,
        currentPageNumber,
        isNextPresent,
        endDate,
        startDate,
      } = response.data;

      setPagination({
        currentPageNumber,
        lastItemIdNext,
        lastItemIdPrev,
        isNextPresent,
        lastSortedColumnValueNext,
        lastSortedColumnValuePrev,
      });

      setDateRange([
        startDate ? moment(startDate) : undefined,
        endDate ? moment(endDate) : undefined,
      ] as RangePickerValue);
    }
    setloading(false);
  };

  const handleBulkRequestRoute = () => {
    history.push(getRfqBulkRequest(), { from: 'Execution' });
  };

  const handleDownload = async () => {
    setDownloading(true);
    const body = getApiCallObject();
    const response = await downloadDailyMisReports(lastRequest ? lastRequest : body);
    if (response.isSuccess) {
      const content = (
        <span>Download in progress, check bulk request tab for more details,
          <span style={{
            textDecoration: 'underline',
            cursor: 'pointer',
            marginLeft: '5px',
            fontWeight: 'bold',
            fontSize: '15px',
          }} onClick={handleBulkRequestRoute}>
            Check
          </span>
        </span>
      );
      message.success(content, 3);
    } else {
      message.error(response.errorMessage);
    }
    setDownloading(false);
  };

  React.useEffect(() => {
    loadMisReports();
  }, [loadData]);

  const getColumnsData = () => {
    const columnData = columns.map((item: string) => {
      return {
        key: item,
        dataIndex: item,
        width: 150,
        title: <div>{item}</div>,
        render: (text, record): React.ReactElement => {
          return record[item] || '-';
        },
      };
    });
    return columnData;
  };

  const columnData = getColumnsData();
  const renderTable = () => {
    return (
      <Table
        bordered={true}
        scroll={{
          x: 1000 + Object.keys(columns).length * 100,
          y: 600,
        }}
        loading={loading}
        pagination={false}
        rowKey={(row) => row.id}
        className={classes.table}
        columns={columnData}
        dataSource={result || []}
      />
    );
  };

  const handlePaginationClick = (nextOrPrev: NextOrPrev) => {
    setPagination({ nextOrPrev });
    setLoadData(!loadData);
  };

  const renderDownloadAndPagination = () => {
    return (
      <div className={classes.flexRow}>
        <SimplePaginator
          styles={{
            marginRight: 12,
          }}
          loading={loading}
          isNext={pagination.isNextPresent}
          onFirstClick={() => handlePaginationClick('first')}
          onPrevClick={() => handlePaginationClick('prev')}
          onNextClick={() => handlePaginationClick('next')}
          pageNumber={pagination.currentPageNumber}
        />
        <Tooltip title="Download Dump">
          <Button icon="download" disabled={downloading || loading} onClick={handleDownload} />
        </Tooltip>
      </div>
    );
  };

  const handleDateRangeFilter = (val: RangePickerValue) => {
    setDateRange(val);
    setLoadData(!loadData);  
  }

  const renderDateFilter = () => {
    return <RangePicker
      disabled={loading}
      onChange={handleDateRangeFilter}
      value={dateRange}
    />
  }

  const renderFeatures = () => {
    return <div className={classes.filters}>
      {isOrgReliance() && renderDateFilter()}
      {renderDownloadAndPagination()}
    </div>;
  }
  if (!columns?.length) {
    return null;
  }
  return (
    <div className={classes.box}>
      <div className={classes.header}>
        <h2
          style={{
            fontWeight: 600,
            marginLeft: 24,
          }}
        >
          {chartName}
        </h2>
        {renderFeatures()}
      </div>
      {renderTable()}
    </div>
  );
};

export default withStyles(styles)(DailyMisReport);
